import { CONFIG_KEY_GOOGLE_ADS_GA4_TRACKING_ID } from '@src/config/env'
import { initGtag } from '@src/telemetry/integrations/GoogleAdsTag'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'
import { TelemetryIntegration } from '../telemetry-events.types'

import { initializeScript } from './provider-utils/initialize-script'

export const GoogleAnalytics4: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.GOOGLE_TAG_MANAGER,
  },

  initialize: async () => {
    initGtag()
    await initializeScript({
      key: 'google-ads-ga4',
      domain: TELEMETRY_DOMAINS.GOOGLE_TAG_MANAGER,
      path: `/gtag/js?id=${CONFIG_KEY_GOOGLE_ADS_GA4_TRACKING_ID}`,
      injectAt: 'beforeFirstScript',
    })
  },

  events: {
    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: properties => {
      if (window.gtag) {
        window.gtag('event', 'view_item', makeEcommEventData(properties))
      }
    },

    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      if (window.gtag) {
        window.gtag('event', 'add_to_cart', makeEcommEventData(properties))
      }
    },

    [TELEMETRY_EVENT_NAMES.ZOOM_PRODUCT_IMAGE]: () => {
      logGAEvent('view_product', 'zoom_product_photo')
    },

    [TELEMETRY_EVENT_NAMES.IS_VIDEO_WATCHED]: () => {
      logGAEvent('view_product', 'is_video_watched')
    },

    [TELEMETRY_EVENT_NAMES.REMOVE_FROM_CART]: properties => {
      if (window.gtag) {
        window.gtag('event', 'remove_from_cart', makeEcommEventData(properties))
      }
    },

    [TELEMETRY_EVENT_NAMES.ACCORDION_TOGGLE]: properties => {
      if (properties.category && properties.action) {
        logGAEvent(properties.category, properties.action, properties.label, properties.value)
      }
    },

    [TELEMETRY_EVENT_NAMES.BUTTON_CLICK]: properties => {
      if (properties.category) {
        logGAEvent(properties.category, properties.action, properties.label, properties.value)
      }
    },

    [TELEMETRY_EVENT_NAMES.LINK_CLICK]: properties => {
      logGAEvent(properties.category, properties.action, properties.label, properties.value)
    },

    [TELEMETRY_EVENT_NAMES.FORM_SUBMIT]: properties => {
      logGAEvent(properties.category, properties.action, properties.label, properties.value)
    },

    [TELEMETRY_EVENT_NAMES.IDME_VERIFICATION]: properties => {
      if (properties.action) {
        logGAEvent(properties.category, properties.action, properties.label, properties.value)
      }
    },

    [TELEMETRY_EVENT_NAMES.SEARCH]: properties => {
      // send a virtual pageview to Google Analytics
      if (window.gtag) {
        window.gtag('send', 'page_view', {
          path: `/?q=${properties.searchQuery ? properties.searchQuery.toLowerCase() : properties.searchQuery}`,
        })
      }
    },

    // [TELEMETRY_EVENT_NAMES.INTERNAL_ANALYTICS]: properties => {
    //   logGAEventForInternalAnalytics(properties)
    // },

    // [TELEMETRY_EVENT_NAMES.MIXPANEL]: properties => {
    //   logGAEventForMixpanel(properties)
    // },
  },
}

// const logGAEventForInternalAnalytics = properties => {
//   window.dataLayer.push({
//     event: 'internal_analytics',
//     internal_analytics_properties: JSON.stringify(properties),
//     internal_analytics_properties_parsed: properties,
//   })
// }

// const logGAEventForMixpanel = properties => {
//   window.dataLayer.push({
//     event: 'mixpanel',
//     properties: JSON.stringify(properties),
//   })
// }

const logGAEvent = (category: string, action: string, label?: unknown, value?: unknown, nonInteraction?: unknown) => {
  const makeEventName = (category: string, action: string) => {
    if (category && action) {
      return `${category}__${action}`.toLowerCase()
    }
    if (category) {
      return category.toLowerCase()
    }
    return action.toLowerCase()
  }
  if (window.gtag) {
    window.gtag('event', makeEventName(category, action), {
      label: label as string,
      value: value as number,
      action,
      category,
      nonInteraction: nonInteraction as string,
    })
  }
}

interface EventData {
  price?: string
  sku?: string
  productTitle?: string
  variantTitle?: string
  quantity?: number
  discount?: number
  vendor?: string
  coupon?: string
  productType?: string
}

export const makeEcommEventData = (properties?: EventData) => {
  const { discount = 0 } = properties ?? {}
  return {
    currency: 'USD',
    value: Number(properties?.price) - discount,
    items: [
      {
        item_id: properties?.sku,
        item_name:
          properties?.productTitle && properties.variantTitle
            ? properties.productTitle + ' - ' + properties.variantTitle
            : '',
        item_category: properties?.productType,
        quantity: properties?.quantity ? properties.quantity : 1,
        price: Number(properties?.price),
        discount,
        item_brand: properties?.vendor,
        item_variant: properties?.variantTitle,
        coupon: properties?.coupon ?? undefined,
      },
    ],
  }
}
